//app/register/choice.tsx
import { StyleSheet, Platform, Animated, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Surface, Button, useTheme, Appbar } from 'react-native-paper';
import { useEffect, useRef, useState } from 'react';
import { registrationSettingsApi } from '@/src/api/services/registrationSettings';
import { verifyRecaptcha } from '@/src/api/apiService';
import { shareLinkReferralCode } from '@/src/utils/shareLinkReferral';
import { currentMobileWebPlatform, type MobileStorePlatform } from '@/src/utils/mobileWebPlatform';
import {
  captureInstallReferrerOnce, isInstallReferrerPending, loadPendingReferral, loadReferralHints, storedReferralCapturedAt,
  recordReferralHint, resolvePendingReferral,
} from '@/src/utils/pendingReferral';
import { checkReferralEligibility } from '@/src/components/referrals/referralEligibility';
import { AppInstallLanding } from '@/src/components/appStores/AppInstallLanding';
import Constants from 'expo-constants';

import ChatBubble from '@/src/components/chat/ChatBubble';
import TypingIndicator from '@/src/components/chat/TypingIndicator';
import ChatConversation from '@/src/components/chat/ChatConversation';
import { track } from '@/src/lib/analytics';
import { AnalyticsEvent } from '@/src/lib/analyticsEvents';

/**
 * Registration opener as a conversation (M13, Ronald 2026-07-08): the funnel
 * greets, "types", asks who you are — persona choice is the first answer of
 * the same chat that continues through /register/owner|driver. All previous
 * behavior preserved: registration_settings gating + web reCAPTCHA.
 */
/** Upper bound for the whole native referral resolution (all server checks). */
const REFERRAL_RESOLUTION_BUDGET_MS = 10_000;

export default function RegisterChoice() {
  const { t } = useTranslation('register');
  const router = useRouter();
  const params = useLocalSearchParams<{ ref?: string | string[] }>();
  // Optional referral code carried from a campaign link; forwarded unchanged
  // into the chosen signup flow (D-057 attribution is only possible at signup).
  const linkReferral = shareLinkReferralCode(params.ref);
  // ALL-22 deferred referral (native app only): the last VALID indication kept
  // on the device (a link opened in the app or the Play install referrer). It
  // is a prefill for the same server-validated signup field, never an
  // attribution. `null` while it is being resolved.
  const [nativeReferral, setNativeReferral] = useState<string | null>(Platform.OS === 'web' ? '' : null);
  // Only while the LOCAL candidate loads; server checks never hold the choice.
  const referralResolving = Platform.OS !== 'web' && nativeReferral === null;
  const stopReferralResolutionRef = useRef<() => void>(() => undefined);
  const [landingDeclined, setLandingDeclined] = useState(false);
  // An explicit incoming code must reach the editable signup field even if a
  // previous valid hint exists. Never silently replace invalid/outage input.
  const referralCode = Platform.OS === 'web' ? (landingDeclined ? '' : linkReferral) : (linkReferral || nativeReferral || '');
  // ALL-22 smart landing: a referral link opened in a phone BROWSER offers the
  // app stores first. Resolved after mount so the static web render matches.
  const [appLanding, setAppLanding] = useState<MobileStorePlatform | 'desktop' | null>(null);
  const theme = useTheme();
  const [registrationStatus, setRegistrationStatus] = useState({
    owner_registration_enabled: true,
    driver_registration_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  // Conversation script: 0 = typing greeting, 1 = greeting shown/typing question,
  // 2 = question shown → options in, 3 = user answered.
  const [stage, setStage] = useState(0);
  const [answer, setAnswer] = useState<string | null>(null);
  const optionsOpacity = useRef(new Animated.Value(0)).current;
  const navigationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const RECAPTCHA_SITE_KEY: string = (Constants.expoConfig?.extra as any)?.RECAPTCHA_SITE_KEY || '6Lcl6q8rAAAAAMFPcJ50s3IDTbOUlMf0D5rlEHd5';

  const loadRecaptchaScript = async (): Promise<void> => {
    if (Platform.OS !== 'web') return;
    if (typeof document === 'undefined') return;
    const existing = document.getElementById('grecaptcha-script') as HTMLScriptElement | null;
    if (existing) return;
    const script = document.createElement('script');
    script.id = 'grecaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  };

  const isLocalWebHost = (): boolean => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
    const appVariant =
      (Constants.expoConfig?.extra as any)?.APP_VARIANT || process.env.APP_VARIANT || 'development';
    if (appVariant !== 'development') return false;
    return /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3})$/i.test(
      window.location.hostname,
    );
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      setLandingDeclined(false);
      setAppLanding(linkReferral ? (currentMobileWebPlatform() ?? 'desktop') : null);
      return;
    }
    // One resolution pass per screen visit. It is SUPERSEDED (and then writes
    // nothing) once the deadline passes, the person picks a persona or the
    // screen unmounts, so a late server answer can never resurrect a code after
    // signup cleared it or replace the one already forwarded.
    let superseded = false;
    const stop = () => { superseded = true; };
    const active = () => !superseded;
    stopReferralResolutionRef.current = stop;
    const deadline = setTimeout(() => {
      stop();
      // Even a stuck local read must not hold the persona choice.
      setNativeReferral(current => current ?? linkReferral);
    }, REFERRAL_RESOLUTION_BUDGET_MS);
    const offer = (code: string) => { if (active()) setNativeReferral(code); };
    // Last VALID first (the accepted record), then the newest unchecked
    // indication; a rejected link with no valid fallback is still offered so
    // the signup field reports it truthfully.
    const pick = (accepted: string | undefined, unverified: string | undefined) =>
      accepted || unverified || linkReferral;
    setNativeReferral(null);
    void (async () => {
      try {
        // The link is only a hint until the server accepts it: an unknown or
        // closed code must never displace an earlier valid one (D-057).
        if (linkReferral) await recordReferralHint(linkReferral, 'link');
      } catch {
        // Not persisted: still offered below for this signup.
      }
      // Local candidate first, without waiting for any network check.
      const local = async () => {
        try {
          const [accepted, hints] = await Promise.all([loadPendingReferral(), loadReferralHints()]);
          // The live link is the newest indication; otherwise the newest hint.
          return pick(accepted?.code, linkReferral || hints[0]?.code);
        } catch {
          return linkReferral;
        }
      };
      const [accepted, referrerPending] = await Promise.all([
        loadPendingReferral().catch(() => null),
        isInstallReferrerPending(),
      ]);
      // First Android launch with nothing else to offer: the unread Play
      // install referrer is a LOCAL source still being read, not an empty
      // answer. Hold the persona choice for it (inside the same budget) so
      // choosing early cannot drop the code. Otherwise offer the local
      // candidate at once; the referrer read and server checks only upgrade it.
      const waitForReferrer = referrerPending && !accepted && !linkReferral;
      if (!waitForReferrer) offer(await local());
      if (!active()) return;
      if (await captureInstallReferrerOnce(undefined, undefined, { shouldContinue: active }) || waitForReferrer) {
        offer(await local());
      }
      if (!active()) return;
      try {
        const result = await resolvePendingReferral(checkReferralEligibility, new Date(), { shouldContinue: active });
        if (result.committed) offer(pick(result.accepted?.code, result.unverified[0]?.code));
      } catch {
        // Storage failure: keep the local candidate already offered.
      }
    })().finally(() => clearTimeout(deadline));
    return () => { stop(); clearTimeout(deadline); };
  }, [linkReferral]);

  useEffect(() => {
    track(AnalyticsEvent.FunnelEntryViewed, {
      entry_point: 'register_choice',
      user_role: 'unknown',
    });
  }, []);

  useEffect(() => {
    const fetchRegistrationStatus = async () => {
      try {
        const status = await registrationSettingsApi.getStatus();
        setRegistrationStatus(status);
      } catch (error) {
        console.error('Failed to fetch registration status:', error);
        setRegistrationStatus({ owner_registration_enabled: true, driver_registration_enabled: true });
      } finally {
        setLoading(false);
      }
    };
    fetchRegistrationStatus();
    if (!isLocalWebHost()) {
      loadRecaptchaScript();
    }
  }, []);

  // Conversation pacing: greeting types in, then the question, then options.
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 700);
    const t2 = setTimeout(() => setStage(2), 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (navigationTimerRef.current) clearTimeout(navigationTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (stage >= 2) {
      Animated.timing(optionsOpacity, { toValue: 1, duration: 300, useNativeDriver: true }).start();
    }
  }, [stage, optionsOpacity]);

  const handleNavigateWithCaptcha = async (path: '/register/driver' | '/register/owner') => {
    if (!mountedRef.current) return;
    const forwarded = referralCode ? `${path}?ref=${encodeURIComponent(referralCode)}` : path;
    if (Platform.OS !== 'web') {
      // A code kept on the device carries its capture time so the server can
      // apply the Admin link validity at signup (D-057); a live link is fresh.
      const capturedAt = referralCode && referralCode !== linkReferral
        ? await storedReferralCapturedAt(referralCode).catch(() => null)
        : null;
      if (!mountedRef.current) return;
      router.push((capturedAt ? `${forwarded}&ref_at=${encodeURIComponent(capturedAt)}` : forwarded) as never);
      return;
    }
    // Google reCAPTCHA site keys reject LAN IPs such as 192.168.x.x by
    // design. Local web development must not be blocked by production-domain
    // verification; backend validation remains active outside local hosts.
    if (isLocalWebHost()) {
      router.push(forwarded as never);
      return;
    }
    try {
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      if (!mountedRef.current) return;
      setVerifying(true);
      await loadRecaptchaScript();
      if (!mountedRef.current) return;
      const grecaptcha = (globalThis as any).grecaptcha;
      if (!grecaptcha || !grecaptcha.execute) {
        // Fail closed on non-local hosts: never navigate without verification
        // when reCAPTCHA is unavailable (blocked script, partial load, no
        // execute). Same recovery as a verification exception.
        alert(t('captchaError'));
        setAnswer(null);
        setStage(2);
        return;
      }
      await new Promise<void>((resolve) => grecaptcha.ready(resolve));
      const token: string = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'register_choice' });
      const result = await verifyRecaptcha(token, 'register_choice');
      if (!mountedRef.current) return;
      if (result.ok) {
        router.push(forwarded as never);
      } else {
        alert(t('captchaFailed'));
        // Reset so the user can choose again — removes the answer bubble,
        // clears the TypingIndicator, and brings the options back.
        setAnswer(null);
        setStage(2);
      }
    } catch (e) {
      if (!mountedRef.current) return;
      console.error('reCAPTCHA verification error:', e);
      alert(t('captchaError'));
      // Same recovery on exception (network failure, script error, etc.).
      setAnswer(null);
      setStage(2);
    } finally {
      if (mountedRef.current) setVerifying(false);
    }
  };

  const choose = (path: '/register/driver' | '/register/owner', label: string) => {
    // The buttons are disabled meanwhile; never decide before the local read.
    if (referralResolving) return;
    // The code is decided now: later answers must not change or write anything.
    stopReferralResolutionRef.current();
    const role = path === '/register/driver' ? 'driver' : 'owner';
    track(AnalyticsEvent.RoleSelected, { user_role: role, role });
    setAnswer(label);
    setStage(3);
    // Small beat so the user sees their answer land before routing.
    if (navigationTimerRef.current) clearTimeout(navigationTimerRef.current);
    navigationTimerRef.current = setTimeout(() => {
      navigationTimerRef.current = null;
      void handleNavigateWithCaptcha(path);
    }, 550);
  };

  if (appLanding && linkReferral) {
    return (
      <Surface style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <AppInstallLanding
          referralCode={linkReferral}
          platform={appLanding === 'desktop' ? null : appLanding}
          onContinueInBrowser={(withoutReferral = false) => {
            setLandingDeclined(withoutReferral);
            setAppLanding(null);
          }}
        />
      </Surface>
    );
  }

  return (
    <Surface style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Appbar.Header>
        <Appbar.BackAction onPress={() => router.back()} />
        <Appbar.Content title={t('registerTitle')} />
      </Appbar.Header>

      <ChatConversation
        composer={
          stage >= 2 && !answer ? (
            <Animated.View style={{ opacity: optionsOpacity, gap: 12 }}>
              <Button
                mode="contained"
                onPress={() => choose('/register/owner', t('chatChoiceOwnerAnswer'))}
                disabled={!registrationStatus.owner_registration_enabled || loading || verifying || referralResolving}
                style={styles.option}
                contentStyle={styles.optionContent}
                testID="chat-choice-owner"
              >
                {registrationStatus.owner_registration_enabled
                  ? t('chatChoiceOwner')
                  : t('ownerButtonDisabled')}
              </Button>
              <Button
                mode="contained-tonal"
                onPress={() => choose('/register/driver', t('chatChoiceDriverAnswer'))}
                disabled={!registrationStatus.driver_registration_enabled || loading || verifying || referralResolving}
                style={styles.option}
                contentStyle={styles.optionContent}
                testID="chat-choice-driver"
              >
                {registrationStatus.driver_registration_enabled
                  ? t('chatChoiceDriver')
                  : t('driverButtonDisabled')}
              </Button>
            </Animated.View>
          ) : null
        }
      >
        {stage === 0 ? (
          <TypingIndicator />
        ) : (
          <ChatBubble role="assistant" testID="chat-greeting">
            {t('chatGreeting')}
          </ChatBubble>
        )}
        {stage === 1 && <TypingIndicator />}
        {stage >= 2 && (
          <ChatBubble
            role="assistant"
            hint={t('chatChoiceHint')}
            testID="chat-choice-question"
            accessibilityRole="header"
            accessibilityLiveRegion="polite"
          >
            {t('chatChoiceQuestion')}
          </ChatBubble>
        )}
        {answer && <ChatBubble role="user">{answer}</ChatBubble>}
        {answer && <TypingIndicator />}
        <View style={{ height: 8 }} />
      </ChatConversation>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  option: { borderRadius: 16 },
  optionContent: { paddingVertical: 10 },
});
